import React, { useEffect, useState, useRef } from 'react'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
const ResultPage: React.FC = () => {
  const { id, setSessionState, uploadSessionState, mode, logActivity, gallery } =
    useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.result
  const [qrCodeImage, setQrCodeImage] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const hasCompleted = useRef(false)

  // Initialize countdown hook
  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)

  // Initial setup effect
  useEffect(() => {
    // Set up countdown callbacks
    resultCountdown.setOnTick(() => {
      setRemainingTime(resultCountdown.getFormattedRemainingSeconds())
    })

    resultCountdown.setOnComplete(() => {
      // Log countdown timeout
      logActivity(`Result page timed out after ${config?.settings.resultTimeout || 120} seconds.`)
      sessionCleanup()
    })

    resultCountdown.start()

    const setupResult = async (): Promise<void> => {
      if (hasCompleted.current) return
      hasCompleted.current = true

      logActivity(`Session completed!`, {
        mode: mode
      })
      setSessionState({
        timeline: {
          isCompleted: true
        }
      })

      // Generate QR code
      const galleryTarget = resolveGallerySessionUrl(id, config?.settings)
      QRCode.toDataURL(galleryTarget, {
        margin: 0,
        width: 300
      }).then(setQrCodeImage)

      const completeSession = async (): Promise<void> => {
        uploadSessionState()
      }

      completeSession()
    }

    setupResult()

    return (): void => {
      resultCountdown.stop()
    }
  }, [])

  // Call all hooks at the top level
  const cachedResultBg = useCachedBackgroundImage('/assets/boothndaughter/result.webp')
  const cachedDoneBg = useCachedBackgroundImage('/assets/boothndaughter/done.webp')
  const cachedStripUrl = useCachedImage(gallery.stripUrl || '')
  const cachedQrCodeImage = useCachedImage(qrCodeImage)

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedResultBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Strip Display - Left Side */}
      {gallery.stripUrl && (
        <div className="absolute right-36 top-72">
          <div className="bg-white p-4 rounded-2xl shadow-2xl">
            <img
              className="w-[350px] h-auto object-contain"
              src={cachedStripUrl}
              alt="Photo Strip"
            />
          </div>
        </div>
      )}

      {/* QR Code - Bottom Center */}
      <div className="absolute bottom-48 left-1/2 transform -translate-x-1/2">
        <div className="bg-white p-8 rounded-3xl shadow-2xl ring-1 ring-carbon/10 flex items-center justify-center">
          {qrCodeImage ? (
            <img className="w-[250px] h-[250px]" src={cachedQrCodeImage} alt="QR Code" />
          ) : (
            <div className="w-[250px] h-[250px] flex items-center justify-center">
              <svg
                className="w-20 h-20 animate-spin text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Remaining Time - Top Right */}
      {(config?.settings?.resultTimeout || 0) > 0 && (
        <div className="absolute top-8 right-8">
          <div className="text-center">
            <span className="block text-white/80 text-2xl mb-2">{remainingTime}</span>
          </div>
        </div>
      )}

      {/* Home Button - Bottom Right */}
      <div className="absolute bottom-16 right-56">
        <button
          onClick={sessionCleanup}
          title="Back to Home"
          style={{
            width: '179px',
            height: '107px',
            backgroundImage: `url(${cachedDoneBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            cursor: 'pointer'
          }}
          className="hover:scale-105 transition-transform"
        />
      </div>
    </div>
  )
}

export default ResultPage
